import {
  Box,
  Alert,
  AlertDescription,
  AlertIcon,
  HStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TableContainer,
  Skeleton,
  Flex,
  Text,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { Bank } from '@finley-technologies/finley-types';
import {
  getAdvanceRate,
  getCollateralDescription,
  getPreAllocationLendableValue,
  getPreAllocationMarketValue,
  getAllocationRate,
  getCollateralAccountStats,
  getPostAllocationLendableValue,
} from '@finley-technologies/valley-bank-utils';
import type { ValleyBankCollateralAccount } from '@finley-technologies/valley-bank-utils';

import { colors } from 'packages/common-frontend/colors';
import { ProgressAlert } from 'packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import {
  frozenHeaderRowStyling,
  frozenMultipleRowCellStyling,
  frozenMultipleHeaderCellStyling,
} from 'packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import {
  useBaseFieldColumnDefinitions,
  isInitiallyVisible,
  isBaseFieldToRename,
  getHeader,
} from 'valleyBank/domains/Collateral/hooks/useBaseFieldColumnDefinitions';
import type {
  HoldingsTableColumnDef,
  Collateral,
  Receivable,
} from 'valleyBank/domains/Collateral/types';
import { useLoanAccountUrlParam } from 'valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';

const IGNORED_CUSTOMER_COLUMN_SET = new Set([
  'allocation',
  'postAllocationLendableValue',
]);

const ORDERED_COLUMN_IDS = [
  'accountNumber',
  'product_name',
  'he_sec_security_num',
  'collateralType',
  'he_sec_sec_desc',
  'loanAccountNumber',
  'marketValue',
  'advanceRate',
  'advanceRateReason',
  'preAllocationLendableValue',
  'allocation',
  'postAllocationLendableValue',
  'he_sec_asset_class',
  'he_sec_issuer_country',
  'he_sec_rating',
  'he_sec_mat_dt',
  'he_sec_pldg_yn',
  'he_secd_pldg_amt_us',
  'he_secd_pldg_amt_fx',
  'he_secd_unpldg_amt_us',
  'he_secd_unpldg_amt_fx',
  'collateralDescription',
];

const FROZEN_COLUMN_IDS = [
  'accountNumber',
  'product_name',
  'he_sec_security_num',
];

const normalizeColumnId = (columnId: string) => {
  if (columnId.startsWith('baseField-')) {
    return columnId.split('-')[1];
  }
  return columnId;
};

const isFrozenColumn = (columnId: string) => {
  const normalizedColumnId = normalizeColumnId(columnId);

  return FROZEN_COLUMN_IDS.includes(normalizedColumnId);
};

const getFrozenColumnWidthBeforeColumn = (
  columnId: string,
  columns: { id: string; width: number }[],
) => {
  const normalizedColumnId = normalizeColumnId(columnId);
  const index = FROZEN_COLUMN_IDS.indexOf(normalizedColumnId);
  if (index === -1) {
    return 0;
  }
  return columns.slice(0, index).reduce((acc, { width }) => acc + width, 0);
};

const sortColumnDefsById = (
  columnDefs: HoldingsTableColumnDef[],
): HoldingsTableColumnDef[] => {
  const output: any[] = Array(columnDefs.length);

  columnDefs.forEach((columnDef) => {
    const { id } = columnDef;
    if (!id) {
      output.push(columnDef);
    } else {
      let normalizedId = id;

      if (id?.startsWith('baseField-')) {
        // in the shape of `baseField-${field}-${loanProductId}`
        normalizedId = id.split('-')[1];
      }

      const index = ORDERED_COLUMN_IDS.indexOf(normalizedId);

      if (index === -1) {
        output.push(columnDef);
      } else {
        output[index] = columnDef;
      }
    }
  });

  return output.filter(Boolean);
};

type CollateralTableBodyProps = {
  totalMarketValue: number;
  effectiveAllocationRate: number;
  totalPostAllocationLendableValue: number;
  effectiveAdvanceRate: number;
  totalPreAllocationLendableValue: number;
  collateral: Collateral[];
  columns: HoldingsTableColumnDef[];
  isDataLoading: boolean;
  isDataError: boolean;
  showAllocation: boolean;
  initialColumnVisibility: VisibilityState;
};
const CollateralTableBody = ({
  collateral,
  columns,
  effectiveAllocationRate,
  totalPreAllocationLendableValue,
  totalMarketValue,
  totalPostAllocationLendableValue,
  effectiveAdvanceRate,
  isDataError,
  isDataLoading,
  showAllocation,
  initialColumnVisibility,
}: CollateralTableBodyProps) => {
  const { loanAccountId } = useLoanAccountUrlParam();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility,
  );

  const table = useReactTable({
    data: collateral,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const frozenTableColumns = table
    .getHeaderGroups()[0]
    .headers.filter((header) => isFrozenColumn(header.column.id));
  const frozenTableColumnWidths = frozenTableColumns.map((header) => ({
    id: header.column.id,
    width: header.getSize() - 16,
  }));

  const tableDataForDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <Flex w="100%" flexWrap="wrap" gridColumnGap="16px" gridRowGap="16px">
        <InfoCard
          title="Total Market Value"
          value={formatCurrencyAmount(totalMarketValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        <InfoCard
          title="Blended Advance Rate"
          value={percentageToPercentageLabel(effectiveAdvanceRate * 100)}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        <InfoCard
          title="Pre Allocation Lendable Value"
          value={formatCurrencyAmount(
            totalPreAllocationLendableValue,
            Bank.Currency.USD,
          )}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        {showAllocation && (
          <>
            <InfoCard
              title="Effective Allocation"
              value={percentageToPercentageLabel(effectiveAllocationRate * 100)}
              icon={<Icon icon="dollar" size="24px" />}
              width="340px"
            />
            <InfoCard
              title="Post Allocation Lendable Value"
              value={formatCurrencyAmount(
                totalPostAllocationLendableValue,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              width="340px"
            />
          </>
        )}
      </Flex>
      {/* @jenDevelops TODO: move filters to make cards obvious they're aggregate of filtered data */}
      {/* @jendevelops TODO implement search and show/hide columns */}
      {/* <HStack w="100%" justify="space-between">
        <HStack spacing="16px">
          <Button
            variant="outline"
            colorScheme="aurora"
            rightIcon={<Icon icon="chevron-down" />}
          >
            Collateral: All
          </Button>
          <Button
            variant="outline"
            colorScheme="aurora"
            rightIcon={<Icon icon="chevron-down" />}
          >
            Security Type: Stocks
          </Button>
          <Button
            variant="outline"
            colorScheme="aurora"
            rightIcon={<Icon icon="chevron-down" />}
          >
            Exchange: All
          </Button>
          <Button
            variant="outline"
            colorScheme="aurora"
            rightIcon={<Icon icon="chevron-down" />}
          >
            Loan Accounts: All
          </Button>
        </HStack>
        <HStack spacing="16px">
          <Button variant="outline" colorScheme="aurora">
            Show/Hide Columns
          </Button>
          <Button
            variant="outline"
            colorScheme="aurora"
            leftIcon={<Icon icon="search" />}
          >
            Search
          </Button>
        </HStack>
      </HStack> */}
      <CsvXlsxDownloadSelect
        dataRows={tableDataForDownload.dataRows}
        headers={tableDataForDownload.headers}
        filenamePrefix={`holdings-loanAccountNumber-${loanAccountId}`}
      />
      <TableContainer
        mt="32px"
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
      >
        <Table>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();

                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      padding={
                        header.column.columnDef.meta?.style?.padding ||
                        '8px 16px 8px 16px'
                      }
                      fontSize="16px"
                      fontWeight="medium"
                      whiteSpace="nowrap"
                      color={colors.black[95]}
                      style={frozenMultipleHeaderCellStyling(
                        isFrozenColumn(header.column.id),
                        getFrozenColumnWidthBeforeColumn(
                          header.column.id,
                          frozenTableColumnWidths,
                        ),
                      )}
                    >
                      <Flex alignItems="center">
                        <Box
                          cursor={columnCanSort ? 'pointer' : 'default'}
                          onClick={header.column.getToggleSortingHandler()}
                          mr="8px"
                          color={colors.aurora[400]}
                          fontWeight="thin"
                        >
                          {{
                            asc: <Icon icon="sort-up" />,
                            desc: <Icon icon="sort-down" />,
                          }[header.column.getIsSorted() as string] ??
                            (columnCanSort ? <Icon icon="sort" /> : null)}
                        </Box>
                        <Box>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {(isDataLoading || isDataError) && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {columns.map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                    >
                      <Skeleton h="100%" />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isDataLoading && !isDataError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                // account for cross collateralization, single receivable can show up multiple times
                const receivableKey = `${row.original.receivable.finley_receivable_id}-${row.original.receivable.loan_account_number}`;
                return (
                  <Tr key={receivableKey}>
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={`${cell.id}+${receivableKey}`}
                        textAlign={cell.column.columnDef.meta?.style?.textAlign}
                        padding={
                          cell.column.columnDef.meta?.style?.padding || '16px'
                        }
                        height="56px"
                        style={frozenMultipleRowCellStyling(
                          isFrozenColumn(cell.column.id),
                          getFrozenColumnWidthBeforeColumn(
                            cell.column.id,
                            frozenTableColumnWidths,
                          ),
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>
      </TableContainer>
      <TanStackPaginationControl table={table} />
    </>
  );
};

const createBaseColumns = ({
  baseFieldColumnDefs,
  loanProductToName,
  ignoreCustomerColumns,
}: {
  baseFieldColumnDefs: HoldingsTableColumnDef[];
  loanProductToName: (loanProductId: string | null) => string;
  ignoreCustomerColumns: Boolean;
}) => {
  const initColumns: HoldingsTableColumnDef[] = [
    // normalized base fields
    {
      id: 'collateralDescription',
      header: 'Collateral Description',
      accessorFn: ({ receivable }) => getCollateralDescription(receivable),
    },
    {
      id: 'accountNumber',
      header: 'Account Number',
      accessorFn: ({ account }) => account.accountNumber,
    },
    {
      id: 'product_name',
      header: 'Product Name',
      accessorFn: ({ receivable }) =>
        receivable.receivableReponseTapeRow.baseFields.product_name,
    },
    {
      id: 'collateralType',

      header: 'Collateral Type',
      accessorFn: ({ receivable }) =>
        loanProductToName(receivable.finley_loan_product_id),
    },
    // rtt base fields
    {
      id: 'loanAccountNumber',

      header: 'Loan Account',
      accessorFn: ({
        receivable: { loan_account_number: loanAccountNumber },
      }) => loanAccountNumber ?? 'Unassigned',
    },
    {
      id: 'marketValue',

      header: 'Market Value',
      accessorFn: ({ receivable }) => getPreAllocationMarketValue(receivable),
      cell: function CollateralMarketValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
    {
      id: 'advanceRate',

      header: 'Advance Rate',
      accessorFn: ({ receivable }) => getAdvanceRate(receivable),
      cell: function AdvanceRate({ getValue }) {
        return percentageToPercentageLabel(getValue() * 100, 2);
      },
    },
    {
      id: 'allocation',
      header: 'Allocation',
      accessorFn: ({ receivable }) => getAllocationRate(receivable),
      cell: function Allocation({ getValue }) {
        return percentageToPercentageLabel(getValue() * 100, 2);
      },
    },
    {
      id: 'postAllocationLendableValue',
      header: 'Post Allocation Lendable Value',
      accessorFn: ({ receivable }) =>
        getPostAllocationLendableValue(receivable),
      cell: function PostAllocationLendableValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
    {
      id: 'advanceRateReason',
      header: 'Advance Rate Reason',
      accessorFn: ({ receivable }) =>
        receivable.receivableReponseTapeRow.borrowingBaseFields.advanceRatePath,
      cell: function AdvanceRateReason({ getValue }) {
        const [
          reasonsString,
          ...modifiersString // array of potential modifier trees
        ] = getValue()?.split('>') ?? [];
        const reasons: string[] = reasonsString.split('#');
        const modifiers: string[][] =
          modifiersString?.map((modifierString: string) =>
            modifierString.split('#'),
          ) ?? [];

        if (reasons.length > 1 && reasons[0] === 'root') {
          reasons.shift();
        }
        return reasons.length > 0 ? (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            gridColumnGap="8px"
            gridRowGap="8px"
          >
            {reasons.map((level, index) => (
              // protecting against not unique reasons because they are not unique outside their level
              <HStack key={`${level}-${uuidV4()}`}>
                <Box
                  bgColor={colors.aurora[100]}
                  p="4px 16px"
                  borderRadius="8px"
                  color="white"
                  w="fit-content"
                >
                  {level === 'root' ? 'default' : level}
                </Box>
                {index < reasons.length - 1 && reasons.length > 0 && (
                  <Icon icon="chevron-right" />
                )}
              </HStack>
            ))}
            {modifiers.length > 0 && (
              <Tooltip
                label={
                  <Flex
                    w="300px"
                    padding="24px"
                    flexDirection="column"
                    gridRowGap="16px"
                  >
                    <Text>Modifiers</Text>
                    <Divider />
                    {modifiers.map((modifierTree) => (
                      <React.Fragment key={JSON.stringify(modifierTree)}>
                        <HStack
                          flexWrap="wrap"
                          key={uuidV4()}
                          spacing="8px"
                          w="100%"
                        >
                          {modifierTree.map((modifier, index) => (
                            <HStack key={uuidV4()}>
                              <Box
                                bgColor={colors.aurora[100]}
                                p="4px 16px"
                                borderRadius="8px"
                                color="white"
                                w="fit-content"
                              >
                                {modifier}
                              </Box>
                              {index < modifierTree.length - 1 &&
                                modifierTree.length > 0 && (
                                  <Icon icon="chevron-right" />
                                )}
                            </HStack>
                          ))}
                        </HStack>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </Flex>
                }
              >
                <HStack p="4px 16px" borderRadius="8px" w="fit-content">
                  <Icon icon="arrow-turn-down-right" />
                  <Text>Modifiers</Text>
                </HStack>
              </Tooltip>
            )}
          </Flex>
        ) : (
          'No advance rate reason'
        );
      },
      enableSorting: false,
    },
    {
      id: 'preAllocationLendableValue',
      header: 'Pre Allocation Lendable Value',
      accessorFn: ({ receivable }) => getPreAllocationLendableValue(receivable),
      cell: function PreAllocationLendableValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
  ];

  const initColumnsWithMetadata: HoldingsTableColumnDef[] = initColumns.map(
    (column) => {
      const newColumnDef = _.cloneDeep(column);
      if (column.id) {
        newColumnDef.header = isBaseFieldToRename(_.snakeCase(column.id))
          ? getHeader(column.id)
          : column.header;
        newColumnDef.meta = {
          initialVisibility: isInitiallyVisible(column.id),
        };
      }
      return newColumnDef;
    },
  );

  const allColumns = _.concat(initColumnsWithMetadata, baseFieldColumnDefs);

  const columns = sortColumnDefsById(allColumns).filter((column) => {
    if (!ignoreCustomerColumns) {
      return true;
    }
    return !IGNORED_CUSTOMER_COLUMN_SET.has(column.id ?? '');
  });

  const initialColumnVisibility = columns.reduce((acc, column) => {
    if (column.id) {
      // eslint-disable-next-line no-param-reassign
      acc[column.id] = column.meta?.initialVisibility ?? true;
    }
    return acc;
  }, {} as VisibilityState);

  return {
    columns,
    initialColumnVisibility,
  };
};

type CollateralTableProps = {
  collateralAccounts: ValleyBankCollateralAccount[];
  isDataLoading: boolean;
  isDataError: boolean;
  showAllocation?: boolean;
  ignoreCustomerColumns: boolean;
};
export const CollateralTable = ({
  collateralAccounts,
  isDataLoading,
  isDataError,
  showAllocation = false,

  // if true, will remove columns that should not appear on customer page
  ignoreCustomerColumns,
}: CollateralTableProps) => {
  // TODO pass in isWip from DataMetadataContext
  const {
    columnDefs: baseFieldColumnDefs,
    isLoading: isBaseFieldConfigsLoading,
    loanProductToName,
  } = useBaseFieldColumnDefinitions();

  // @vb - @jen from vince - can I do this safely
  // review later
  const collateral: Collateral[] = Object.values(
    collateralAccounts
      .flatMap(({ receivables, ...account }) => {
        return receivables.map((receivable: Receivable) => ({
          account,
          receivable,
        }));
      })
      .reduce<{ [key: string]: Collateral }>((acc, collat) => {
        if (acc[collat.receivable.finley_receivable_id]) {
          // eslint-disable-next-line no-param-reassign
          acc[
            collat.receivable.finley_receivable_id
            // eslint-disable-next-line camelcase
          ].receivable.loan_account_number += `, ${collat.receivable.loan_account_number}`;
        } else {
          // eslint-disable-next-line no-param-reassign
          acc[collat.receivable.finley_receivable_id] = {
            ...collat,
            receivable: {
              ...collat.receivable,
            },
          };
        }

        return acc;
      }, {}),
  );

  const stats = getCollateralAccountStats(collateralAccounts);

  const { columns, initialColumnVisibility } = createBaseColumns({
    loanProductToName,
    baseFieldColumnDefs,
    ignoreCustomerColumns,
  });

  return (
    <>
      {!isDataLoading &&
        !isDataError &&
        isBaseFieldConfigsLoading &&
        collateral.length === 0 && (
          <ProgressAlert status="info">Loading holdings</ProgressAlert>
        )}
      {!isDataLoading &&
        !isDataError &&
        !isBaseFieldConfigsLoading &&
        collateral.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>No Collateral found</AlertDescription>
          </Alert>
        )}
      {/* even if data error or loading, show skeleton to indicate table is loading */}
      {!isBaseFieldConfigsLoading && collateral.length > 0 && (
        <CollateralTableBody
          showAllocation={showAllocation}
          totalMarketValue={stats.totalMarketValue}
          effectiveAllocationRate={stats.effectiveAllocationRate}
          totalPostAllocationLendableValue={
            stats.totalPostAllocationLendableValue
          }
          effectiveAdvanceRate={stats.effectiveAdvanceRate}
          totalPreAllocationLendableValue={
            stats.totalPreAllocationLendableValue
          }
          collateral={collateral}
          columns={columns}
          isDataLoading={isDataLoading}
          isDataError={isDataError}
          initialColumnVisibility={initialColumnVisibility}
        />
      )}
    </>
  );
};
